import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { getPublicTeacher } from "../utils/publicProfile";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import JoinCourseCard from "./Components/JoinCourseCard";
import { formatCourseDate, sessionStatus } from "../utils/courseTime";
import { priceIfIJoin, formatPieces } from "../utils/pricing";
import { FaCalendarAlt, FaUserGraduate, FaCoins } from "react-icons/fa";
import { BsStars } from "react-icons/bs";
import { useLang } from "../i18n/LanguageContext";

const Teacher = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();

  const [teacher, setTeacher] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const param = decodeURIComponent(teacherId);
        // Anciens liens /Teacher/<e-mail> : plus utilisés (les e-mails ne sont plus publics)
        if (param.includes("@")) return;
        const prof = await getPublicTeacher(param);
        setTeacher(prof);
        if (!prof) return;
        const csnap = await getDocs(query(collection(db, "courses"), where("ownerId", "==", param)));
        setCourses(
          csnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((c) => sessionStatus(c) !== "ended")
            .sort((a, b) =>
              `${a.date || ""}${a.time || ""}`.localeCompare(`${b.date || ""}${b.time || ""}`)
            )
        );
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <HeaderComp />
        <div className="flex-1 flex items-center justify-center text-stone-400">{t("Chargement...")}</div>
        <Footer />
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="min-h-screen flex flex-col">
        <HeaderComp />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <div className="text-5xl">🔮</div>
          <p className="text-stone-500">{t("Professeur introuvable.")}</p>
          <button onClick={() => navigate(-1)} className="text-prune underline text-sm">{t("Retour")}</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-creme flex flex-col">
      <HeaderComp />

      {/* ─── Hero Professeur ─── */}
      <div className="bg-prune text-white py-12 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
          {/* Avatar */}
          <div className="w-36 h-36 rounded-full border-4 border-or overflow-hidden shrink-0">
            {teacher.profilePicture ? (
              <img src={teacher.profilePicture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-prune-dark flex items-center justify-center text-5xl font-bold">
                {teacher.firstName ? teacher.firstName[0] : "?"}
              </div>
            )}
          </div>

          {/* Infos */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center gap-2 justify-center md:justify-start mb-1">
              <BsStars className="text-or" />
              <span className="text-white/80 text-sm uppercase tracking-wider font-semibold">
                {t("Professeur Tatie Soso")}
              </span>
            </div>
            <h1 className="text-3xl font-bold mb-4">
              {teacher.firstName} {teacher.lastName}
            </h1>

            <p className="text-white/90 leading-relaxed max-w-xl">
              {teacher.description || t("Passionné·e par le partage des connaissances, ce professeur est là pour t'aider à progresser.")}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto w-full px-6 py-12 flex-1">
        {/* ─── Cours à venir ─── */}
        <h2
          className="text-3xl text-prune mb-8 text-center"
          style={{ fontFamily: "Alegreya, Georgia, serif", fontWeight: 700 }}
        >
          {t("Cours à venir")}
        </h2>

        {courses.length === 0 ? (
          <div className="text-center py-10 text-stone-400">
            <div className="text-4xl mb-3">📚</div>
            <p>{t("Aucun cours prévu pour l'instant.")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
            {courses.map((course) => {
              const myPrice = priceIfIJoin(course);
              const spotsLeft = course.maxStudents - (course.students || 0);
              return (
                <div key={course.id} className="bg-white rounded-2xl shadow-carte overflow-hidden hover:shadow-carte-hover transition-shadow">
                  <div className="bg-or px-5 py-3">
                    <h3 className="text-white font-bold">{course.name}</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <p className="text-stone-600 text-sm">{course.description}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-500">
                      <span className="flex items-center gap-1"><FaCalendarAlt className="text-prune" /> {formatCourseDate(course, lang)}</span>
                      <span className="flex items-center gap-1"><FaUserGraduate className="text-prune" /> {course.students || 0}/{course.maxStudents}</span>
                      <span className="flex items-center gap-1"><FaCoins className="text-or" /> {t("Prix {price} · tu payes {mine}", { price: formatPieces(course.price, lang), mine: formatPieces(myPrice, lang) })}</span>
                    </div>
                    <button
                      onClick={() => setSelectedCourse(course)}
                      disabled={spotsLeft === 0}
                      className={`w-full py-2.5 rounded-full font-semibold text-sm mt-2 transition-all ${
                        spotsLeft === 0
                          ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                          : "bg-prune text-white hover:bg-prune-dark"
                      }`}
                    >
                      {spotsLeft === 0 ? t("Complet") : t("Rejoindre")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ─── Avis ─── */}
        <div className="bg-or rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white text-center mb-6">{t("Avis")}</h3>
          <p className="text-center text-orange-100 text-sm italic">{t("Les avis des élèves apparaîtront ici prochainement.")}</p>
        </div>
      </div>

      <Footer />

      {selectedCourse && (
        <JoinCourseCard course={selectedCourse} onClose={() => setSelectedCourse(null)} />
      )}
    </div>
  );
};

export default Teacher;
